import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { useIdentity } from "../lib/identity";
import { supabase } from "../lib/supabase";
import {
  fetchPortalBySlug,
  logPortalVisit,
  type PortalData,
} from "../lib/portal-api";
import { unifiedLogin } from "../lib/unified-auth";
import { Eye, EyeOff, Building2, ArrowRight, ShieldCheck, Users, Award } from "lucide-react";

export default function PortalPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const { identity, setIdentity } = useIdentity();

  const [portal, setPortal] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (!companySlug) return;
    fetchPortalBySlug(companySlug).then((data) => {
      if (data) {
        setPortal(data);
        logPortalVisit(data.id, "view");
      } else {
        setNotFound(true);
      }
      setLoading(false);
    });
  }, [companySlug]);

  // If already logged in and belongs to this org, redirect to dashboard
  useEffect(() => {
    if (identity.isAuthenticated && portal && identity.organizationId === portal.id) {
      navigate("/dashboard");
    }
  }, [identity, portal, navigate]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginStatus("submitting");
    setLoginError(null);

    if (portal) {
      logPortalVisit(portal.id, "login_attempt", email);
    }

    const { identity: id, error } = await unifiedLogin({ email, password });
    if (error || !id) {
      setLoginError(error ?? "Login failed.");
      setLoginStatus("error");
      return;
    }

    // Check if user belongs to this organization
    if (portal && id.organizationId !== portal.id) {
      setLoginError("This account does not belong to this company portal.");
      setLoginStatus("error");
      await supabase.auth.signOut();
      return;
    }

    setIdentity(id);
    setLoginStatus("idle");
    navigate("/dashboard");
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-subtle)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1.125rem", color: "var(--text-muted)" }}>Loading portal...</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-subtle)" }}>
        <div style={{ textAlign: "center", maxWidth: "400px" }}>
          <Building2 size={48} style={{ color: "var(--text-muted)", marginBottom: "1rem" }} />
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Portal Not Found</h1>
          <p className="text-muted" style={{ marginBottom: "1.5rem" }}>
            No company portal exists at this URL. Check the link or register a new company.
          </p>
          <Link to="/register" className="btn btn-primary">Register a Company</Link>
        </div>
      </div>
    );
  }

  const primaryColor = portal?.portal_primary_color ?? "#1e3a5f";

  return (
    <>
      <SEO
        title={`${portal?.name ?? "Company"} Portal — Sentinel`}
        description={`Login to ${portal?.name ?? "your company"}'s Sentinel certification portal.`}
        canonical={`/portal/${companySlug}`}
      />

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {/* Branded header */}
        <header
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
            padding: "var(--space-6) var(--space-4)",
            color: "#fff",
          }}
        >
          <div style={{ maxWidth: "900px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {portal?.logo_url ? (
                <img
                  src={portal.logo_url}
                  alt={portal.name}
                  style={{ width: "64px", height: "64px", objectFit: "contain", borderRadius: "var(--radius-md)", background: "#fff", padding: "0.5rem" }}
                />
              ) : (
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "var(--radius-md)",
                    background: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Building2 size={28} />
                </div>
              )}
              <div>
                <h1 style={{ color: "#fff", fontSize: "1.5rem", marginBottom: "0.25rem" }}>{portal?.name}</h1>
                <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.875rem", margin: 0 }}>
                  {portal?.portal_tagline ?? "Sentinel Certification Portal"}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <img src="/sentinel-white.png" alt="Sentinel" style={{ width: "24px", height: "24px" }} />
              <span style={{ fontWeight: 700, fontSize: "0.875rem" }}>Sentinel</span>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--space-6) var(--space-4)", background: "var(--bg-subtle)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-6)", maxWidth: "900px", width: "100%" }}>
            {/* Left: Login form */}
            <div className="card" style={{ padding: "var(--space-6)" }}>
              <h2 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Employee Login</h2>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
                Sign in with your company email and password to access your training.
              </p>

              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label" htmlFor="portal-email">Email</label>
                  <input
                    id="portal-email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="portal-password">Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="portal-password"
                      type={showPassword ? "text" : "password"}
                      className="form-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      style={{ paddingRight: "2.5rem" }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "0.625rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-muted)",
                        padding: 0,
                      }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {loginError && <p className="form-error">{loginError}</p>}

                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  disabled={loginStatus === "submitting"}
                  style={{ marginTop: "var(--space-3)" }}
                >
                  {loginStatus === "submitting" ? "Signing in..." : "Sign In"}
                  {loginStatus !== "submitting" && <ArrowRight size={16} style={{ marginLeft: "0.5rem" }} />}
                </button>
              </form>

              <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-4)", borderTop: "1px solid var(--border)" }}>
                <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.5rem" }}>
                  New employee? Register with your department access code:
                </p>
                <Link
                  to={`/portal/${companySlug}/register`}
                  className="btn btn-secondary btn-block"
                  style={{ fontSize: "0.875rem" }}
                >
                  Register with Access Code
                </Link>
              </div>
            </div>

            {/* Right: Info panel */}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              <div className="card" style={{ padding: "var(--space-4)" }}>
                <ShieldCheck size={24} style={{ color: "var(--accent-600)", marginBottom: "0.5rem" }} />
                <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>Certified Training</h3>
                <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>
                  Access industry-specific Sentinel safety certifications tailored to {portal?.industry ?? "your industry"}.
                </p>
              </div>

              <div className="card" style={{ padding: "var(--space-4)" }}>
                <Users size={24} style={{ color: "var(--accent-600)", marginBottom: "0.5rem" }} />
                <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>Team Management</h3>
                <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>
                  Your company manages your training assignments, tracks your progress, and issues branded certificates.
                </p>
              </div>

              <div className="card" style={{ padding: "var(--space-4)" }}>
                <Award size={24} style={{ color: "var(--accent-600)", marginBottom: "0.5rem" }} />
                <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>Recognized Credentials</h3>
                <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>
                  Earn Sentinel certifications with your company logo, recognized across the industry.
                </p>
              </div>
            </div>
          </div>
        </main>

        <footer style={{ padding: "var(--space-4)", textAlign: "center", fontSize: "0.75rem", color: "var(--text-muted)", borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
          {portal?.name} — Powered by Sentinel Certification Platform
        </footer>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .portal-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
